<template>
  <nav
    class="fixed top-0 h-auto md:h-14 z-50 w-full bg-white border-gray-200 dark:bg-bgMeduimDark dark:border-gray-700 no-print modern-header pb-2 md:pb-0"
  >
    <div :class="'px-3 py-1 lg:px-5 ' + (isRtl ? ' lg:pr-3 ' : 'lg:pl-3')">
      <div class="flex flex-wrap md:flex-nowrap items-center justify-between gap-2">
        <div class="flex items-center justify-start">
          <!-- Mobile Sidebar Toggle -->
          <button
            data-drawer-target="logo-sidebar"
            data-drawer-toggle="logo-sidebar"
            aria-controls="logo-sidebar"
            type="button"
            class="inline-flex items-center p-2 text-sm text-gray-500 dark:text-gray-300 rounded-lg md:hidden hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200"
            @click="isSidebarOpen = !isSidebarOpen"
          >
            <span class="sr-only">Open sidebar</span>
            <svg
              class="w-6 h-6"
              :class="{ 'flip-horizontal': isRtl }"
              aria-hidden="true"
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                clip-rule="evenodd"
                fill-rule="evenodd"
                d="M2 4.75A.75.75 0 012.75 4h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 4.75zm0 10.5a.75.75 0 01.75-.75h7.5a.75.75 0 010 1.5h-7.5a.75.75 0 01-.75-.75zM2 10a.75.75 0 01.75-.75h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 10z"
              ></path>
            </svg>
          </button>

          <!-- Desktop Sidebar Collapse/Expand Button -->
          <button
            type="button"
            class="hidden md:flex items-center p-2 mx-2 text-sm text-gray-500 dark:text-gray-300 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200"
            @click="toggleSidebar"
          >
            <span class="sr-only">Toggle sidebar</span>
            <svg
              class="w-4 h-4 text-gray-800 dark:text-white transition-transform duration-300 ease-in-out"
              :class="{
                'rotate-180': isSidebarCollapsed,
                'rotate-0': !isSidebarCollapsed,
              }"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 8 14"
            >
              <path
                stroke="currentColor"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M7 1 1.3 6.326a.91.91 0 0 0 0 1.348L7 13"
              />
            </svg>
          </button>

          <div class="flex flex-col">
            <a
              href="/"
              :class="
                'flex mx-10 text-xl dark:text-textWhite hidden md:flex' +
                (isRtl ? ' md:ml-24 ' : 'md:mr-24')
              "
            >
              <img
                src="../../assets/logo.png"
                alt=""
                class="w-16 lg:w-24 mx-3"
              />
            </a>
          </div>
          <div class="flex items-center space-x-1.5 bg-blue-50 dark:bg-blue-900/30 px-2 md:px-3 py-1 md:py-1.5 rounded-full mx-2 md:mx-5">
            <i class="fa-solid fa-dollar-sign font-bold text-blue-600 dark:text-blue-400 text-xs md:text-sm"></i>
            <div class="w-1"></div>
            <span class="text-xs md:text-sm font-bold text-blue-700 dark:text-blue-300">
              {{ $t("dolar_price") }}: {{ formatNumber(dolar_price) }}
            </span>
          </div>
        </div>
        <div
          class="relative w-full md:max-w-xl md:mx-auto z-50 order-last md:order-none"
          v-if="
            this.$store.getters['checkUserAccess']({
              id: '40',
              action: 'view',
            })
          "
        >
          <!-- Input -->
          <div class="relative w-full">
            <!-- Search Icon or Loading Spinner -->
            <div
              class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
            >
              <!-- Loading Spinner -->
              <div
                v-if="isSearching"
                class="animate-spin h-5 w-5 text-blue-500"
              >
                <svg
                  class="h-5 w-5"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    class="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="4"
                  ></circle>
                  <path
                    class="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              </div>
              <!-- Search Icon -->
              <svg
                v-else
                class="h-5 w-5 text-gray-400 dark:text-gray-500"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M21 21l-4.35-4.35m0 0A7.5 7.5 0 104.5 4.5a7.5 7.5 0 0012.15 12.15z"
                />
              </svg>
            </div>

            <!-- Input -->
            <input
              v-model="query"
              type="text"
              :placeholder="$t('smart_search')"
              class="mb-0 dark:mb-2 w-full pl-10 pr-4 py-2 md:py-2.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm text-sm md:text-base text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:focus:ring-blue-500 dark:focus:border-blue-500 transition-all duration-200"
            />
          
          </div>

          <!-- Dropdown -->
          <div
            v-if="query && (Object.keys(results).length || isSearching || searchError)"
            class="absolute w-full bg-white dark:bg-gray-800 mt-1 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 max-h-96 overflow-y-auto z-50 modern-search-scroll"
          >
            <!-- Loading State -->
            <div
              v-if="isSearching"
              class="flex items-center justify-center py-8"
            >
              <div class="flex items-center space-x-3">
                <div class="animate-spin h-5 w-5 text-blue-500">
                  <svg
                    class="h-5 w-5"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      class="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      stroke-width="4"
                    ></circle>
                    <path
                      class="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                </div>
                <span class="text-sm text-gray-500 dark:text-gray-400">
                  {{ $t('searching') }}...
                </span>
              </div>
            </div>

            <!-- Error State -->
            <div
              v-else-if="searchError"
              class="flex items-center justify-center py-8"
            >
              <div class="text-center">
                <div class="text-red-500 mb-2">
                  <i class="fa-solid fa-exclamation-triangle text-xl"></i>
                </div>
                <p class="text-sm text-gray-500 dark:text-gray-400">
                  {{ $t('search_error') }}
                </p>
              </div>
            </div>

            <!-- No Results State -->
            <div
              v-else-if="!isSearching && Object.keys(results).length === 0"
              class="flex items-center justify-center py-8"
            >
              <div class="text-center">
                <div class="text-gray-400 mb-2">
                  <i class="fa-solid fa-search text-xl"></i>
                </div>
                <p class="text-sm text-gray-500 dark:text-gray-400">
                  {{ $t('no_results_found') }}
                </p>
                <p class="text-xs text-gray-400 dark:text-gray-500 mt-1">
                  {{ $t('try_different_keywords') }}
                </p>
              </div>
            </div>

            <!-- Results -->
            <div
              v-else
              v-for="(group, groupKey) in results"
              :key="groupKey"
              class="p-2"
            >
              <!-- Group title -->
              <div class="text-xs font-semibold text-gray-500 dark:text-gray-400 px-2 py-1">
                {{ formatGroupName(groupKey) }}
              </div>

              <!-- Items -->
              <div
                v-for="(item, i) in group"
                :key="i"
                class="flex items-center gap-3 px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer rounded transition-colors duration-200"
                @click="goToPage(groupKey, item)"
              >
                <!-- Avatar -->
                <div
                  class="w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-sm"
                  :style="{ backgroundColor: getColor(item, groupKey) }"
                >
                  {{ getInitials(item, groupKey) }}
                </div>

                <!-- Text info -->
                <div class="flex-1">
                  <div class="text-sm font-medium text-gray-800 dark:text-gray-200">
                    {{ getTitle(item, groupKey) }}
                  </div>
                  <div class="text-xs text-gray-500 dark:text-gray-400">
                    {{ getSubtext(item, groupKey) }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="flex items-center">
          <div
            class="mx-0.5 md:mx-1"
            v-if="
              this.$store.getters['checkUserAccess']({
                id: '38',
                action: 'add',
              })
            "
          >
            <button
              type="button"
              class="flex relative text-sm rounded-full text-gray-600 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 p-1.5 md:p-2 transition-all duration-200"
              id="darkModeToggle"
              @click="backup"
            >
              <span class="sr-only">DarkMode</span>
              <i class="fa-solid fa-download text-sm md:text-lg"></i>
            </button>
          </div>
          <div class="mx-0.5 md:mx-2">
            <button
              @click="toggleTheme"
              type="button"
              class="flex relative text-sm rounded-full text-gray-600 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 p-1.5 md:p-2 transition-all duration-200"
              id="darkModeToggle"
            >
              <span class="sr-only">DarkMode</span>
              <i class="fa-solid fa-moon text-sm md:text-lg" v-if="!isDark"></i>
              <i class="fa-solid fa-sun text-sm md:text-lg" v-if="isDark"></i>
            </button>
          </div>

          <!-- Calculator Button -->
          <div class="mx-0.5 md:mx-2">
            <button
              @click="openCalculator"
              type="button"
              class="flex relative text-sm rounded-full text-gray-600 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-blue-600 dark:hover:text-blue-400 p-1.5 md:p-2 transition-all duration-200"
              title="Calculator"
            >
              <span class="sr-only">Calculator</span>
              <i class="fa-solid fa-calculator text-sm md:text-lg"></i>
            </button>
          </div>

          <!--user profile-->
          <div class="flex items-center mx-1">
            <div>
              <button
                type="button"
                class="flex text-sm rounded-full items-center space-x-1 hover:bg-gray-100 dark:hover:bg-gray-700 p-2 transition-all duration-200"
                aria-expanded="false"
                data-dropdown-toggle="dropdown-user"
              >
                <span
                  class="font-semibold text-gray-700 dark:text-gray-200 px-1"
                >
                  <div
                    class="flex text-sm rounded-full items-center space-x-1"
                    v-if="currentUser != '' && currentUser != null"
                  >
                    <span
                      class="font-semibold text-gray-600 dark:text-textWhite1 px-1 text-sm"
                      >{{ currentUser.name }}</span
                    >
                  </div></span
                >
              </button>
            </div>
            <div class="">
              <button
                type="button"
                class="flex text-sm rounded-full items-center space-x-1"
                v-if="currentUser != '' && currentUser != null"
                @click="logout"
              >
                <span>
                  <i
                    class="fa fa-sign-out text-red-600 text-lg"
                    aria-hidden="true"
                  ></i>
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </nav>

<aside
  id="logo-sidebar"
  :class="
    'fixed z-40 top-20 md:top-14 bottom-0 transition-transform overflow-y-auto bg-white dark:bg-bgLightDark modern-sidebar-scroll ' +
    (isRtl ? ' right-0 ' : ' left-0 ') +
    (isSidebarCollapsed ? ' w-20' : ' w-60') +
    (isSidebarOpen
      ? '' /* top-14 is already applied */
      : isRtl
      ? ' translate-x-full '
      : ' -translate-x-full ') +
    ' ' +
    (!isMobile ? ' md:translate-x-0' : '') +
    ' ' +
    (isMobile ? '' : ' md:block ')
  "
  aria-label="Sidebar"
>
  <div class="h-full px-4 md:pt-16 pt-6 pb-4 bg-white dark:bg-bgLightDark">
    <ul class="space-y-2 font-semibold">
      <li v-for="item in sidebarItems" :key="item.id">
        <button
          type="button"
          :class="
            'flex items-center w-full p-2 text-sm text-textBlack4 dark:text-textWhite2 transition duration-75 rounded-lg group ' +
            (isActive(item)
              ? 'bg-primaryColor text-textWhite hover:text-textWhite '
              : ' hover:text-primaryColor')
          "
          aria-controls="dropdown-sale"
          data-collapse-toggle="dropdown-sale"
          @click="toggleUl(item)"
          v-if="item.access"
        >
          <i :class="item.icon"></i>
          <span
            v-if="!isSidebarCollapsed"
            :class="
              'flex-1 mx-1 whitespace-nowrap flex justify-between ' +
              (isRtl ? ' text-right' : ' text-left')
            "
          >
            <span
              :class="
                item.id == 'items' && expireCount > 0 ? ' text-red-500' : ''
              "
            >
              {{ item.name }}</span
            >
            <span
              v-if="item.id == 'items' && expireCount > 0"
              class="text-lg text-red-500 mx-3"
            >
              {{ expireCount }}</span
            >
            <a
              :href="item.tutorial"
              target="_blank"
              v-if="
                item.subItems.length == 0 &&
                $store.getters['checkTutorialAccess']
              "
            >
              <i
                :class="
                  'fa-brands fa-youtube p-1 text-sm ' +
                  (isActive(item) ? 'text-textWhite' : 'text-primaryColor')
                "
              ></i> </a
          ></span>
          <svg
            class="w-2.5 h-2.5"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 10 6"
            v-show="item.subItems.length > 0 && !isSidebarCollapsed"
          >
            <path
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="1.5"
              d="m1 1 4 4 4-4"
            />
          </svg>
        </button>
        <ul
          id="dropdown-sale"
          class="py-2 space-y-2 mx-2"
          v-show="item.isOpen && !isSidebarCollapsed"
        >
          <li v-for="subItem in item.subItems" :key="subItem.id">
            <button
              @click="routeTo(subItem)"
              :class="
                'text-sm flex text-right items-center justify-between w-full p-1 text-textBlack5 dark:text-textWhite3 transition duration-75 rounded-lg px-2 group hover:text-primaryColor ' +
                (isActiveSubItem(subItem)
                  ? 'text-primaryColor hover:text-textWhite'
                  : ' hover:text-primaryColor')
              "
              v-if="subItem.access"
            >
              <span
                :class="
                  subItem.id == 'expire-item' && expireCount > 0
                    ? ' text-red-500'
                    : ''
                "
              >
                {{ subItem.text }}</span
              >

              <span
                v-if="subItem.id == 'expire-item' && expireCount > 0"
                class="text-lg text-red-500"
              >
                {{ expireCount }}</span
              >
              <a
                :href="subItem.tutorial"
                target="_blank"
                v-if="
                  $store.getters['checkTutorialAccess'] &&
                  subItem.no_tutorial != true
                "
              >
                <i
                  class="fa-brands fa-youtube text-primaryColor p-1 text-sm"
                ></i>
              </a>
            </button>
          </li>
        </ul>
      </li>
    </ul>
  </div>
</aside>

  <div class="h-20 md:h-5"></div>
  <transition name="fade">
    <div class="popup-modal overflow-y-auto" v-if="isSidebarOpen">
      <div class="window">
        <slot></slot>
      </div>
    </div>
  </transition>

  <!-- Calculator Modal -->
  <CalculatorModal 
    :isOpen="isCalculatorOpen" 
    @close="closeCalculator"
    @open-calculator="openCalculator"
  />
</template>

<script>
let debounceTimer;

import { formatNumber } from "../../utils/functions";
import { SERVER_URL } from "../../constants/config";
import { formatDate } from "../../utils/functions";
import CalculatorModal from "../Modals/CalculatorModal.vue";
export default {
  props: {
    msg: String,
  },
  data() {
    return {
      isSidebarOpen: false,
      isDark: false,
      ulVisibility: {}, // Initialize with an empty object
      sidebarItems: [
        {
          id: "dashboard",
          name: this.$t("dashboard"),
          isOpen: false,
          icon: "fa-solid fa-gauge-high px-2 ",
          href: "/",
          subItems: [],
          tutorial: "https://youtu.be/MoJCnN1F3iE",
          access: this.$store.getters["checkUserAccess"]({
            id: "1",
            action: "view",
          }),
        },
        {
          id: "items-settings",
          name: this.$t("items_settings"),
          icon: "fa-solid fa-boxes-stacked px-2",
          href: "/",
          isOpen: false,
          access:
            this.$store.getters["checkUserAccess"]({
              id: "2",
              action: "add",
            }) ||
            this.$store.getters["checkUserAccess"]({
              id: "2",
              action: "view",
            }) ||
            this.$store.getters["checkUserAccess"]({
              id: "3",
              action: "view",
            }) ||
            this.$store.getters["checkUserAccess"]({
              id: "3",
              action: "add",
            }) ||
            this.$store.getters["checkUserAccess"]({
              id: "4",
              action: "view",
            }) ||
            this.$store.getters["checkUserAccess"]({
              id: "4",
              action: "add",
            }),
          subItems: [
            {
              id: "add-unit",
              text: this.$t("add_item_unit"),
              href: "/item-unit",
              tutorial: "https://youtu.be/Q1oloj-gWLM",
              access:
                this.$store.getters["checkUserAccess"]({
                  id: "2",
                  action: "add",
                }) ||
                this.$store.getters["checkUserAccess"]({
                  id: "2",
                  action: "view",
                }),
            },
            {
              id: "ca1",
              text: this.$t("item_categorys"),
              href: "/item-category",
              tutorial: "https://youtu.be/yOQpnJ5HN60",
              access:
                this.$store.getters["checkUserAccess"]({
                  id: "3",
                  action: "add",
                }) ||
                this.$store.getters["checkUserAccess"]({
                  id: "3",
                  action: "view",
                }),
            },
            {
              id: "caw1",
              text: this.$t("waste_items_categorys"),
              href: "/item-waste-category",
              tutorial: "https://youtu.be/iaLaeHFK3vA",
              access:
                this.$store.getters["checkUserAccess"]({
                  id: "4",
                  action: "add",
                }) ||
                this.$store.getters["checkUserAccess"]({
                  id: "4",
                  action: "view",
                }),
            },
            {
              id: "brand1",
              text: this.$t("brands"),
              href: "/brand",
              access:
                this.$store.getters["checkUserAccess"]({
                  id: "5",
                  action: "add",
                }) ||
                this.$store.getters["checkUserAccess"]({
                  id: "5",
                  action: "view",
                }),
            },
            {
              id: "accords-note1",
              text: this.$t("accords_notes"),
              href: "/accords-note",
              access:
                this.$store.getters["checkUserAccess"]({
                  id: "7",
                  action: "add",
                }) ||
                this.$store.getters["checkUserAccess"]({
                  id: "7",
                  action: "view",
                }),
            },
          ],
        },
        {
          id: "froshtn",
          name: this.$t("sale_settings"),
          icon: "fa-solid fa-shop px-2",
          href: "/",
          isOpen: false,
          access:
            this.$store.getters["checkUserAccess"]({
              id: "5",
              action: "add",
            }) ||
            this.$store.getters["checkUserAccess"]({
              id: "5",
              action: "view",
            }) ||
            this.$store.getters["checkUserAccess"]({
              id: "6",
              action: "view",
            }) ||
            this.$store.getters["checkUserAccess"]({
              id: "6",
              action: "add",
            }) ||
            this.$store.getters["checkUserAccess"]({
              id: "7",
              action: "view",
            }) ||
            this.$store.getters["checkUserAccess"]({
              id: "7",
              action: "add",
            }),
          subItems: [
            {
              id: "add-type",
              text: this.$t("sale_types"),
              href: "/add-sale-type",
              tutorial: "https://youtu.be/zEY8Quk833w",
              access:
                this.$store.getters["checkUserAccess"]({
                  id: "5",
                  action: "add",
                }) ||
                this.$store.getters["checkUserAccess"]({
                  id: "5",
                  action: "view",
                }),
            },
            {
              id: "platform-froshtn",
              text: this.$t("sale_platforms"),
              href: "/sale-platform",
              tutorial: "https://youtu.be/ocN28yg0YtU",
              access:
                this.$store.getters["checkUserAccess"]({
                  id: "6",
                  action: "add",
                }) ||
                this.$store.getters["checkUserAccess"]({
                  id: "6",
                  action: "view",
                }),
            },
            {
              id: "platform-frosht1n",
              text: this.$t("sale_citys"),
              href: "/sale-city",
              tutorial: "https://youtu.be/M0Vlw7jNp4s",
              access:
                this.$store.getters["checkUserAccess"]({
                  id: "7",
                  action: "add",
                }) ||
                this.$store.getters["checkUserAccess"]({
                  id: "7",
                  action: "view",
                }),
            },
          ],
        },
        {
          id: "items",
          name: this.$t("items"),
          icon: "fa-solid fa-boxes-stacked px-2",
          href: "/",
          isOpen: false,
          access:
            this.$store.getters["checkUserAccess"]({
              id: "8",
              action: "add",
            }) ||
            this.$store.getters["checkUserAccess"]({
              id: "8",
              action: "view",
            }) ||
            this.$store.getters["checkUserAccess"]({
              id: "9",
              action: "add",
            }) ||
            this.$store.getters["checkUserAccess"]({
              id: "11",
              action: "view",
            }) ||
            this.$store.getters["checkUserAccess"]({
              id: "12",
              action: "view",
            }) ||
            this.$store.getters["checkUserAccess"]({
              id: "13",
              action: "add",
            }) ||
            this.$store.getters["checkUserAccess"]({
              id: "13",
              action: "view",
            }),
          subItems: [
            {
              id: "add-item",
              text: this.$t("add_new_item"),
              href: "/add-item",
              tutorial: "https://youtu.be/nH1YNokZzPM",
              access: this.$store.getters["checkUserAccess"]({
                id: "8",
                action: "add",
              }),
            },
            {
              id: "add-item-from-csv",
              text: this.$t("add_item_from_csv"),
              href: "/add-item-from-csv",
              tutorial: "https://youtu.be/DZ5WGu3hayI",
              access: this.$store.getters["checkUserAccess"]({
                id: "9",
                action: "add",
              }),
            },
            {
              id: "item-list",
              text: this.$t("items_list"),
              href: "/item-list",
              tutorial: "https://youtu.be/nH1YNokZzPM",
              access: this.$store.getters["checkUserAccess"]({
                id: "8",
                action: "view",
              }),
            },
            {
              id: "shortage-item",
              text: this.$t("shortage_items"),
              href: "/shortage-item",
              tutorial: "https://youtu.be/Sfv47TbWBmM",
              access: this.$store.getters["checkUserAccess"]({
                id: "11",
                action: "view",
              }),
            },
            {
              id: "expire-item",
              text: this.$t("expire_items"),
              href: "/expire-item",
              tutorial: "https://youtu.be/Sfv47TbWBmM",
              access: this.$store.getters["checkUserAccess"]({
                id: "12",
                action: "view",
              }),
            },
            {
              id: "waste-item",
              text: this.$t("waste_items"),
              href: "/waste-item",
              tutorial: "https://youtu.be/Pi9AKEjOzNI",
              access:
                this.$store.getters["checkUserAccess"]({
                  id: "13",
                  action: "view",
                }) ||
                this.$store.getters["checkUserAccess"]({
                  id: "13",
                  action: "add",
                }),
            },
            //not-moved-items
            {
              id: "not-moved-items",
              text: this.$t("not_moved_items"),
              href: "/not-moved-items",
              tutorial: "#",
              access: this.$store.getters["checkUserAccess"]({
                id: "45",
                action: "view",
              }),
            },
          ],
        },
        {
          id: "box",
          name: this.$t("boxes"),
          icon: "fa-solid fa-box px-2",
          href: "/",
          isOpen: false,
          access:
            this.$store.getters["checkUserAccess"]({
              id: "14",
              action: "view",
            }) ||
            this.$store.getters["checkUserAccess"]({
              id: "14",
              action: "add",
            }) ||
            this.$store.getters["checkUserAccess"]({
              id: "15",
              action: "view",
            }) ||
            this.$store.getters["checkUserAccess"]({
              id: "15",
              action: "add",
            }) ||
            this.$store.getters["checkUserAccess"]({
              id: "16",
              action: "view",
            }) ||
            this.$store.getters["checkUserAccess"]({
              id: "16",
              action: "add",
            }),
          subItems: [
            {
              id: "add-box",
              text: this.$t("add_boxes"),
              href: "/all-box",
              tutorial: "https://youtu.be/FcCD2kKSWJQ",
              access:
                this.$store.getters["checkUserAccess"]({
                  id: "14",
                  action: "view",
                }) ||
                this.$store.getters["checkUserAccess"]({
                  id: "14",
                  action: "add",
                }),
            },
            {
              id: "exchange-box",
              text: this.$t("exchange_inside_boxes"),
              href: "/exchange-box",
              tutorial: "https://youtu.be/FdMIu48oBko",
              access:
                this.$store.getters["checkUserAccess"]({
                  id: "15",
                  action: "view",
                }) ||
                this.$store.getters["checkUserAccess"]({
                  id: "15",
                  action: "add",
                }),
            },
            {
              id: "transfer-box",
              text: this.$t("transfer_between_boxes"),
              href: "/transfer-box",
              tutorial: "https://youtu.be/u0cuaFq1RDs",
              access:
                this.$store.getters["checkUserAccess"]({
                  id: "16",
                  action: "view",
                }) ||
                this.$store.getters["checkUserAccess"]({
                  id: "16",
                  action: "add",
                }),
            },
          ],
        },
        {
          id: "purchase",
          name: this.$t("purchase"),
          icon: "fa-solid fa-shopping-cart px-2",
          href: "/",
          isOpen: false,
          access:
            this.$store.getters["checkUserAccess"]({
              id: "17",
              action: "view",
            }) ||
            this.$store.getters["checkUserAccess"]({
              id: "17",
              action: "add",
            }) ||
            this.$store.getters["checkUserAccess"]({
              id: "18",
              action: "view",
            }) ||
            this.$store.getters["checkUserAccess"]({
              id: "18",
              action: "add",
            }),
          subItems: [
            {
              id: "new-purchase",
              text: this.$t("new_purchase"),
              href: "/new-purchase",
              tutorial: "https://youtu.be/oCCl38mhkw8",
              access: this.$store.getters["checkUserAccess"]({
                id: "17",
                action: "add",
              }),
            },
            {
              id: "purchase-list",
              text: this.$t("purchases_list"),
              href: "/purchase-list",
              tutorial: "https://youtu.be/tA-r8wwQ6EE",
              access: this.$store.getters["checkUserAccess"]({
                id: "17",
                action: "view",
              }),
            },
            {
              id: "new-return-purchase",
              text: this.$t("return_item_to_suplier"),
              href: "/new-return-purchase",
              tutorial: "https://youtu.be/TAvL_Ku-JHM",
              access: this.$store.getters["checkUserAccess"]({
                id: "18",
                action: "add",
              }),
            },
            {
              id: "return-purchases",
              text: this.$t("return_purchases"),
              href: "/return-purchases",
              tutorial: "https://youtu.be/TAvL_Ku-JHM",
              access: this.$store.getters["checkUserAccess"]({
                id: "18",
                action: "view",
              }),
            },
          ],
        },
        {
          id: "froshtn",
          name: this.$t("sale"),
          icon: "fa-solid fa-scale-balanced px-2",
          href: "/",
          isOpen: false,
          access:
            this.$store.getters["checkUserAccess"]({
              id: "19",
              action: "add",
            }) ||
            this.$store.getters["checkUserAccess"]({
              id: "19",
              action: "view",
            }) ||
            this.$store.getters["checkUserAccess"]({
              id: "20",
              action: "view",
            }) ||
            this.$store.getters["checkUserAccess"]({
              id: "20",
              action: "add",
            }),
          subItems: [
            {
              id: "sale-pos",
              text: this.$t("new_sale"),
              href: "/sale-pos",
              tutorial: "https://youtu.be/1VYPsTE2iic",
              access: this.$store.getters["checkUserAccess"]({
                id: "19",
                action: "add",
              }),
            },
            {
              id: "sold-list",
              text: this.$t("solds"),
              href: "/sold-list",
              tutorial: "https://youtu.be/8ILOtGxFbqY",
              access: this.$store.getters["checkUserAccess"]({
                id: "19",
                action: "view",
              }),
            },
            {
              id: "return-invoice",
              text: this.$t("return_invoices"),
              href: "/return-invoice",
              tutorial: "https://youtu.be/h5LjeY68qHY",
              access:
                this.$store.getters["checkUserAccess"]({
                  id: "20",
                  action: "add",
                }) ||
                this.$store.getters["checkUserAccess"]({
                  id: "20",
                  action: "view",
                }),
            },
          ],
        },

        {
          id: "debt",
          name: this.$t("debts"),
          icon: "fa-solid fa-file-invoice-dollar px-2",
          href: "/",
          isOpen: false,
          access:
            this.$store.getters["checkUserAccess"]({
              id: "21",
              action: "add",
            }) ||
            this.$store.getters["checkUserAccess"]({
              id: "21",
              action: "view",
            }),
          subItems: [
            {
              id: "add-debt",
              text: this.$t("add_new_debt"),
              href: "/add-debt",
              tutorial: "https://youtu.be/93j405tBPPo",
              access: this.$store.getters["checkUserAccess"]({
                id: "21",
                action: "add",
              }),
            },
            {
              id: "debt-list",
              text: this.$t("debts_list"),
              href: "/debt-list",
              tutorial: "https://youtu.be/ySsvIaYRNtA",
              access: this.$store.getters["checkUserAccess"]({
                id: "21",
                action: "view",
              }),
            },
          ],
        },

        {
          id: "expense",
          name: this.$t("expenses"),
          icon: "fa-solid fa-sack-dollar px-2",
          href: "/",
          isOpen: false,
          access:
            this.$store.getters["checkUserAccess"]({
              id: "22",
              action: "add",
            }) ||
            this.$store.getters["checkUserAccess"]({
              id: "22",
              action: "view",
            }) ||
            this.$store.getters["checkUserAccess"]({
              id: "23",
              action: "add",
            }) ||
            this.$store.getters["checkUserAccess"]({
              id: "23",
              action: "view",
            }),
          subItems: [
            {
              id: "expense-category",
              text: this.$t("expenses_categorys"),
              href: "/expense-category",
              tutorial: "https://youtu.be/Wh1BELkQUNo",
              access:
                this.$store.getters["checkUserAccess"]({
                  id: "22",
                  action: "add",
                }) ||
                this.$store.getters["checkUserAccess"]({
                  id: "22",
                  action: "view",
                }),
            },
            {
              id: "add-expense",
              text: this.$t("add_new_expense"),
              href: "/add-expense",
              tutorial: "https://youtu.be/tdCyP_D5WmM",
              access: this.$store.getters["checkUserAccess"]({
                id: "23",
                action: "add",
              }),
            },
            {
              id: "expense-list",
              text: this.$t("expenses_list"),
              href: "/expense-list",
              tutorial: "https://youtu.be/2QyvV-MvUiE",
              access: this.$store.getters["checkUserAccess"]({
                id: "23",
                action: "view",
              }),
            },
          ],
        },
        {
          id: "income",
          name: this.$t("incomes"),
          icon: "fa-solid fa-hand-holding-dollar px-2",
          href: "/",
          isOpen: false,
          access:
            this.$store.getters["checkUserAccess"]({
              id: "24",
              action: "add",
            }) ||
            this.$store.getters["checkUserAccess"]({
              id: "24",
              action: "view",
            }) ||
            this.$store.getters["checkUserAccess"]({
              id: "25",
              action: "add",
            }) ||
            this.$store.getters["checkUserAccess"]({
              id: "25",
              action: "view",
            }),
          subItems: [
            {
              id: "income-category",
              text: this.$t("incomes_categorys"),
              href: "/income-category",
              tutorial: "https://youtu.be/RIGhzJ7gPeU",
              access:
                this.$store.getters["checkUserAccess"]({
                  id: "24",
                  action: "add",
                }) ||
                this.$store.getters["checkUserAccess"]({
                  id: "24",
                  action: "view",
                }),
            },
            {
              id: "add-income",
              text: this.$t("add_new_income"),
              href: "/add-income",
              tutorial: "https://youtu.be/wdYdGovSSlU",
              access: this.$store.getters["checkUserAccess"]({
                id: "25",
                action: "add",
              }),
            },
            {
              id: "income-list",
              text: this.$t("incomes_list"),
              href: "/income-list",
              tutorial: "https://youtu.be/YOofhXfAuLA",
              access: this.$store.getters["checkUserAccess"]({
                id: "25",
                action: "view",
              }),
            },
          ],
        },

        {
          id: "report",
          name: this.$t("reports"),
          icon: "fa-solid fa-file-invoice-dollar px-2",
          href: "/",
          isOpen: false,
          access: this.$store.getters["checkUserAccess"]({
            id: "26",
            action: "view",
          }),
          subItems: [
            {
              id: "all-yearly-report",
              text: this.$t("annual_sales_report"),
              href: "/all-yearly-report",
              tutorial: "https://www.youtube.com/watch?v=1QX6Z8QV9ZM",
              no_tutorial: true,
              access: true,
            },
            {
              id: "category-report",
              text: this.$t("category_sales_reports"),
              href: "/category-report",
              tutorial: "https://www.youtube.com/watch?v=1QX6Z8QV9ZM",
              access: true,
              no_tutorial: true,
            },
            {
              id: "city-report",
              text: this.$t("city_sales_reports"),
              href: "/city-report",
              tutorial: "https://www.youtube.com/watch?v=1QX6Z8QV9ZM",
              access: true,
              no_tutorial: true,
            },
            {
              id: "platform-report",
              text: this.$t("platform_sales_reports"),
              href: "/platform-report",
              tutorial: "https://www.youtube.com/watch?v=1QX6Z8QV9ZM",
              access: true,
              no_tutorial: true,
            },
            {
              id: "sale-report",
              text: this.$t("sale_type_sales_reports"),
              href: "/sale-report",
              tutorial: "https://www.youtube.com/watch?v=1QX6Z8QV9ZM",
              access: true,
              no_tutorial: true,
            },
            {
              id: "employee-report",
              text: this.$t("employee_sales_reports"),
              href: "/employee-report",
              tutorial: "https://www.youtube.com/watch?v=1QX6Z8QV9ZM",
              access: true,
              no_tutorial: true,
            },
            {
              id: "item-report",
              text: this.$t("item_sales_reports"),
              href: "/item-report",
              tutorial: "https://www.youtube.com/watch?v=1QX6Z8QV9ZM",
              access: true,
              no_tutorial: true,
            },
            {
              id: "loss-items-report",
              text: this.$t("loss_items_report"),
              href: "/loss-items-report",
              tutorial: "https://www.youtube.com/watch?v=1QX6Z8QV9ZM",
              access: true,
              no_tutorial: true,
            },
            {
              id: "customer-report",
              text: this.$t("customer_sales_reports"),
              href: "/customer-report",
              tutorial: "https://www.youtube.com/watch?v=1QX6Z8QV9ZM",
              access: true,
              no_tutorial: true,
            },
            {
              id: "waste-category-report",
              text: this.$t("waste_category_reports"),
              href: "/waste-category-report",
              tutorial: "https://www.youtube.com/watch?v=1QX6Z8QV9ZM",
              access: true,
              no_tutorial: true,
            },
            {
              id: "waste-item-report",
              text: this.$t("waste_item_reports"),
              href: "/waste-item-report",
              tutorial: "https://www.youtube.com/watch?v=1QX6Z8QV9ZM",
              access: true,
              no_tutorial: true,
            },
            {
              id: "expense-category-report",
              text: this.$t("expense_category_reports"),
              href: "/expense-category-report",
              tutorial: "https://www.youtube.com/watch?v=1QX6Z8QV9ZM",
              access: true,
              no_tutorial: true,
            },
            {
              id: "expense-customer-report",
              text: this.$t("expense_customer_reports"),
              href: "/expense-customer-report",
              tutorial: "https://www.youtube.com/watch?v=1QX6Z8QV9ZM",
              access: true,
              no_tutorial: true,
            },
            {
              id: "income-customer-report",
              text: this.$t("income_customer_reports"),
              href: "/income-customer-report",
              tutorial: "https://www.youtube.com/watch?v=1QX6Z8QV9ZM",
              access: true,
              no_tutorial: true,
            },
            {
              id: "income-category-report",
              text: this.$t("income_category_reports"),
              href: "/income-category-report",
              tutorial: "https://www.youtube.com/watch?v=1QX6Z8QV9ZM",
              access: true,
              no_tutorial: true,
            },
            {
              id: "debt-report",
              text: this.$t("debt_reports"),
              href: "/debt-report",
              tutorial: "https://www.youtube.com/watch?v=1QX6Z8QV9ZM",
              access: true,
              no_tutorial: true,
            },
            {
              id: "suplier-report",
              text: this.$t("suplier_purchase_reports"),
              href: "/suplier-report",
              tutorial: "https://www.youtube.com/watch?v=1QX6Z8QV9ZM",
              access: true,
              no_tutorial: true,
            },
            {
              id: "purchase-item-report",
              text: this.$t("item_purchase_reports"),
              href: "/purchase-item-report",
              tutorial: "https://www.youtube.com/watch?v=1QX6Z8QV9ZM",
              access: true,
              no_tutorial: true,
            },
          ],
        },

        {
          id: "role",
          name: this.$t("roles"),
          icon: "fa-solid fa-file-invoice-dollar px-2",
          href: "/",
          isOpen: false,
          access:
            this.$store.getters["checkUserAccess"]({
              id: "27",
              action: "view",
            }) ||
            this.$store.getters["checkUserAccess"]({
              id: "27",
              action: "add",
            }),
          subItems: [
            {
              id: "add-role",
              text: this.$t("add_new_role"),
              href: "/add-role",
              tutorial: "https://youtu.be/zfJFWHZAFUs",
              access: this.$store.getters["checkUserAccess"]({
                id: "27",
                action: "add",
              }),
            },
            {
              id: "role-list",
              text: this.$t("roles_list"),
              href: "/role-list",
              tutorial: "https://youtu.be/zfJFWHZAFUs",
              access: this.$store.getters["checkUserAccess"]({
                id: "27",
                action: "view",
              }),
            },
          ],
        },
        {
          id: "app-user",
          name: this.$t("app_users"),
          icon: "fa-solid fa-mobile-screen-button px-2",
          href: "/app-user",
          isOpen: false,
          subItems: [],
          access:
            this.$store.getters["checkUserAccess"]({
              id: "6",
              action: "view",
            }) ||
            this.$store.getters["checkUserAccess"]({
              id: "6",
              action: "add",
            }),
        },
        {
          id: "notification",
          name: this.$t("notifications") || "Notifications",
          icon: "fa-solid fa-bell px-2",
          href: "/notification",
          isOpen: false,
          subItems: [],
          access:
            this.$store.getters["checkUserAccess"]({
              id: "33",
              action: "view",
            }) ||
            this.$store.getters["checkUserAccess"]({
              id: "33",
              action: "add",
            }),
        },
        {
          id: "employee",
          name: this.$t("employees"),
          icon: "fas fa-users px-2",
          href: "/employee",
          isOpen: false,
          tutorial: "https://youtu.be/fALcl72zmHs",

          subItems: [],
          access:
            this.$store.getters["checkUserAccess"]({
              id: "28",
              action: "view",
            }) ||
            this.$store.getters["checkUserAccess"]({
              id: "28",
              action: "add",
            }),
        },
        {
          id: "customers",
          name: this.$t("customers"),
          icon: "fa-solid fa-users px-2",
          href: "/customer",
          isOpen: false,
          tutorial: "https://youtu.be/F3AtTpnhq8k",
          subItems: [],
          access:
            this.$store.getters["checkUserAccess"]({
              id: "29",
              action: "view",
            }) ||
            this.$store.getters["checkUserAccess"]({
              id: "29",
              action: "add",
            }),
        },
        {
          id: "supliers",
          name: this.$t("supliers"),
          icon: "fa-solid fa-boxes-packing px-2",
          href: "/suplier",
          isOpen: false,
          tutorial: "https://youtu.be/-gn2lcG61xI",
          subItems: [],
          access:
            this.$store.getters["checkUserAccess"]({
              id: "30",
              action: "view",
            }) ||
            this.$store.getters["checkUserAccess"]({
              id: "30",
              action: "add",
            }),
        },
        {
          id: "dolar_price",
          name: this.$t("dolar_price"),
          icon: "fa-solid fa-hand-holding-dollar px-2",
          href: "/dolar-price",
          isOpen: false,
          tutorial: "https://youtu.be/RyQutn7C3RE",
          subItems: [],
          access:
            this.$store.getters["checkUserAccess"]({
              id: "31",
              action: "view",
            }) ||
            this.$store.getters["checkUserAccess"]({
              id: "31",
              action: "add",
            }),
        },
        {
          id: "note",
          name: this.$t("notes"),
          icon: "fa-solid fa-note-sticky px-2",
          href: "/note",
          isOpen: false,
          tutorial: "https://youtu.be/xlDBnoA8VPo",
          subItems: [],
          access:
            this.$store.getters["checkUserAccess"]({
              id: "32",
              action: "view",
            }) ||
            this.$store.getters["checkUserAccess"]({
              id: "32",
              action: "add",
            }),
        },
        //slider 39
        {
          id: "slider",
          name: this.$t("sliders"),
          icon: "fas fa-ad px-2",
          href: "/slider",
          isOpen: false,
          tutorial: "",
          subItems: [],
          access:
            this.$store.getters["checkUserAccess"]({
              id: "39",
              action: "view",
            }) ||
            this.$store.getters["checkUserAccess"]({
              id: "39",
              action: "add",
            }),
        },

        {
          id: "barcode",
          name: this.$t("barcode"),
          icon: "fa-solid fa-barcode px-2",
          href: "/barcode",
          isOpen: false,
          tutorial: "https://youtu.be/QGzjb5TIyug",
          subItems: [],
          access: this.$store.getters["checkUserAccess"]({
            id: "33",
            action: "add",
          }),
        },
        {
          id: "change_password",
          name: this.$t("change_password"),
          icon: "fa-solid fa-unlock-keyhole px-2",
          href: "/change-password",
          isOpen: false,
          tutorial: "https://youtu.be/E2nAO0xgJTA",
          subItems: [],
          access: this.$store.getters["checkUserAccess"]({
            id: "34",
            action: "edit",
          }),
        },
        {
          id: "settings",
          name: this.$t("settings"),
          icon: "fa-solid fa-gear px-2",
          href: "/settings",
          isOpen: false,
          tutorial: "https://youtu.be/JuFZmYKLpCg",
          subItems: [],
          access: this.$store.getters["checkUserAccess"]({
            id: "35",
            action: "edit",
          }),
        },
        {
          id: "history-log",
          name: this.$t("activity_history"),
          icon: "fa-solid fa-history px-2",
          href: "/history-log",
          isOpen: false,
          tutorial: "https://youtu.be/uSYqVS-1uZU",
          subItems: [],
          access: this.$store.getters["checkUserAccess"]({
            id: "37",
            action: "view",
          }),
        },
        {
          id: "faq",
          name: this.$t("faq"),
          icon: "fa-solid fa-question px-2",
          href: "/faq",
          isOpen: false,
          tutorial: "https://youtu.be/DpMa4-F7Pbc",
          subItems: [],
          access: this.$store.getters["checkUserAccess"]({
            id: "36",
            action: "edit",
          }),
        },
        // Add more sidebar items as needed
      ],
      expireCount: 0,
      query: "",
      results: {},
      isSearching: false,
      searchError: null,
      isCalculatorOpen: false,
    };
  },
  watch: {
    query(newVal) {
      clearTimeout(debounceTimer);
      if (!newVal.trim()) {
        this.results = {};
        this.isSearching = false;
        this.searchError = null;
        return;
      }
      debounceTimer = setTimeout(() => {
        this.fetchResults();
      }, 800);
    },
  },
  computed: {
    isSidebarCollapsed() {
      return this.$store.state.navbar.isSidebarCollapsed;
    },
    dolar_price() {
      return this.$store.state.navbar.dp_price;
    },
    currentUser() {
      return this.$store.state.navbar.currentUser;
    },
    isMobile() {
      //check by window width
      return window.innerWidth <= 768;
    },
    isRtl() {
      return (
        localStorage.getItem("lang_wise_manager") == "ar" ||
        localStorage.getItem("lang_wise_manager") == "ku" ||
        //if null
        localStorage.getItem("lang_wise_manager") == null ||
        //if empty
        localStorage.getItem("lang_wise_manager") == ""
      );
    },
    currentSetting() {
      return this.$store.state.settings.settings;
    },
  },
  components: {
    CalculatorModal,
  },
  created() {
    this.isDark = localStorage.getItem("theme") == "dark";
    this.getData();
    this.getSettings();
    // Store sidebar items in Vuex store for access by other components
    this.$store.dispatch("navbar/setSidebarItems", this.sidebarItems);
  },

  methods: {
    openCalculator() {
      this.isCalculatorOpen = true;
    },
    closeCalculator() {
      this.isCalculatorOpen = false;
    },
    async fetchResults() {
      try {
        this.isSearching = true;
        this.searchError = null;
        const res = await this.$store.dispatch("navbar/globalSearch", {
          query: this.query,
        });
        this.results = res.results || {};
      } catch (error) {
        console.error('Search error:', error);
        this.searchError = error.message || 'Search failed';
        this.results = {};
      } finally {
        this.isSearching = false;
      }
    },

    getInitials(item, group) {
      let name = this.getTitle(item, group);
      return name ? name.substring(0, 2).toUpperCase() : "؟";
    },

    getColor(item, group) {
      const colors = [
        "#2563eb",
        "#059669",
        "#7c3aed",
        "#d97706",
        "#db2777",
        "#111827",
      ];
      const hash = group.length + JSON.stringify(item).length;
      return colors[hash % colors.length];
    },

    getTitle(item, group) {
      switch (group) {
        case "suplier":
          return item.su_name;
        case "customer":
          return item.cu_name;
        case "item":
          return item.i_name;
        case "invoice":
          return this.$t("invoice_number") + ` #${item.in_id}`;
        case "purchase":
          return this.$t("purchase") + ` #${item.pu_id}`;
        case "returned_purchase":
          return this.$t("returned_purchase") + ` #${item.rep_id}`;
        default:
          return "Unknown";
      }
    },

    getSubtext(item, group) {
      switch (group) {
        case "suplier":
          return `${
            this.$t("total_debt_dinar") +
            " : " +
            this.formatNumber(item.current_debt.IQD) +
            " " +
            this.$t("dinar") +
            " " +
            " | " +
            this.$t("total_debt_dollar") +
            " : " +
            this.formatNumber(item.current_debt.USD) +
            "$"
          }`;
        case "customer":
          return `${
            this.$t("total_debt_dinar") +
            " : " +
            this.formatNumber(item.current_debt.IQD) +
            " " +
            this.$t("dinar") +
            " | " +
            this.$t("total_debt_dollar") +
            " : " +
            this.formatNumber(item.current_debt.USD) +
            "$"
          }`;
        case this.$t("item"):
          return (
            `${this.$t("barcode")} : ${item.i_barcode}` +
            ` | ${this.$t("price")}: ${this.formatNumber(
              item.i_sale_price
            )} ${this.$t(item.currency.cur_name)}`
          );
        case "invoice":
          return (
            (item.created_at ? `${this.formatDate(item.created_at)}` : "") +
            ` | ${item.customer.cu_name}`
          );
        case "purchase":
          return (
            (item.created_at ? `${this.formatDate(item.created_at)}` : "") +
            ` | ${item.suplier.su_name}`
          );
        case "returned_purchase":
          return (
            (item.created_at ? `${this.formatDate(item.created_at)}` : "") +
            ` | ${item.suplier.su_name}`
          );
        default:
          return item.created_at ? `${this.formatDate(item.created_at)}` : "";
      }
    },

    formatGroupName(key) {
      const map = {
        suplier: "دابینکەرەکان",
        customer: "کڕیارەکان",
        item: "کاڵاکان",
        invoice: "وەسڵەکانی فرۆشتن",
        purchase: "کڕینەکان",
        returned_purchase: "وەسڵە گەڕێندراوەکان بۆ دابینکەر",
      };
      return map[key] || key;
    },

    goToPage(group, item) {
      let id = "";
      let path = "";

      switch (group) {
        case "suplier":
          id = item.su_id;
          path = `/suplier-account/${id}`;
          break;
        case "customer":
          id = item.cu_id;
          path = `/customer-account/${id}`;
          break;
        case "item":
          id = item.i_id;
          path = `/item-detail/${id}`;
          break;
        case "invoice":
          id = item.in_id;
          path = `/sale-pos/${id}`;
          break;
        case "purchase":
          id = item.pu_id;
          path = `/edit-purchase/${id}`;

          break;
        case "returned_purchase":
          id = item.rep_id;
          path = `/edit-return-purchase/${id}`;
          break;
      }

      if (path) {
        this.$router.push(path);
        this.query = "";
        this.results = {};
      }
    },

    toggleSidebar() {
      this.$store.commit("navbar/TOGGLE_SIDEBAR");
    },
    async getSettings() {
      await this.$store.dispatch("settings/getSettings", {});

      localStorage.setItem(
        "currentSettingWiseManager",
        JSON.stringify(this.currentSetting)
      );
      var isHasAccessExpire = this.$store.getters["checkUserAccess"]({
        id: "12",
        action: "view",
      });
      if (isHasAccessExpire) {
        var res = await this.$store.dispatch("navbar/getExpiredCount", {});
        this.expireCount = res.data;
      }
    },
    formatNumber(number) {
      return formatNumber(number);
    },
    formatDate(date) {
      return formatDate(date);
    },
    logout() {
      //clear the token
      localStorage.removeItem("token_wise_manager");
      localStorage.removeItem("user_wise_manager");
      window.location.href = "/login";
    },
    getData() {
      this.$store.dispatch("navbar/getItems");
      this.$store.dispatch("navbar/getCurrentUser");
    },
    /* backup() {
      this.$store.dispatch("navbar/backup");
    },*/
    backup() {
      const token = localStorage.getItem("token_wise_manager");
      const url = SERVER_URL + `backup?token=${token}`;

      window.open(url, "_blank");
      // Alternatively, use the fetch API:
      // fetch(url, { headers: { 'Authorization': 'Bearer ' + token } });
    },
    toggleUl(item) {
      if (item.subItems.length > 0) {
        item.isOpen = !item.isOpen;
      }
      if (item.subItems.length == 0) {
        this.$router.push(item.href);
      }
    },
    routeTo(item) {
      this.$router.push(item.href);
    },
    isActive(item) {
      if (item.subItems.length == 0) {
        return this.$route.path == item.href;
      }
      //if any subItem is active return true
      return item.subItems.some((subItem) => {
        if (this.$route.path == subItem.href) {
          item.isOpen = true;
        }

        return this.$route.path == subItem.href;
      });
    },
    isActiveSubItem(item) {
      return this.$route.path == item.href;
    },
    toggleTheme() {
      this.isDark = !this.isDark;
      localStorage.setItem("theme", this.isDark ? "dark" : "light");

      //reload the page
      window.location.reload();
    },
  },
};
</script>
<style scoped>
/* css class for the transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.popup-modal {
  background-color: rgba(0, 0, 0, 0.5);
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  z-index: 5;
}

.window {
  border-radius: 5px;
  margin-left: auto;
  margin-right: auto;
  padding: 1rem;
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

/* Modern Sidebar Scrollbar */
.modern-sidebar-scroll {
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 #f1f5f9;
}

.modern-sidebar-scroll::-webkit-scrollbar {
  width: 6px;
}

.modern-sidebar-scroll::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 3px;
}

.modern-sidebar-scroll::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
  transition: background 0.2s ease;
}

.modern-sidebar-scroll::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* Dark mode scrollbar */
.dark .modern-sidebar-scroll {
  scrollbar-color: #475569 #1e293b;
}

.dark .modern-sidebar-scroll::-webkit-scrollbar-track {
  background: #1e293b;
}

.dark .modern-sidebar-scroll::-webkit-scrollbar-thumb {
  background: #475569;
}

.dark .modern-sidebar-scroll::-webkit-scrollbar-thumb:hover {
  background: #64748b;
}

/* Modern Search Dropdown Scrollbar */
.modern-search-scroll {
  scrollbar-width: thin;
  scrollbar-color: #e2e8f0 #f8fafc;
}

.modern-search-scroll::-webkit-scrollbar {
  width: 4px;
}

.modern-search-scroll::-webkit-scrollbar-track {
  background: #f8fafc;
  border-radius: 2px;
}

.modern-search-scroll::-webkit-scrollbar-thumb {
  background: #e2e8f0;
  border-radius: 2px;
  transition: background 0.2s ease;
}

.modern-search-scroll::-webkit-scrollbar-thumb:hover {
  background: #cbd5e1;
}

/* Dark mode search scrollbar */
.dark .modern-search-scroll {
  scrollbar-color: #334155 #0f172a;
}

.dark .modern-search-scroll::-webkit-scrollbar-track {
  background: #0f172a;
}

.dark .modern-search-scroll::-webkit-scrollbar-thumb {
  background: #334155;
}

.dark .modern-search-scroll::-webkit-scrollbar-thumb:hover {
  background: #475569;
}
</style>
